import styled, { css } from "styled-components/macro";
import {
  brandPrimary70,
  darkModeBackgroundHoveredColor,
  darkModeDarkTextColor,
  darkModeModalBackgroundColor,
  darkModeTitleBackgroundColor,
  lightBorder3,
  lightNeutral10,
  lightTextBody,
  lightTextOnDark,
} from "constants/colors";
import { ChevronRight } from "@mui/icons-material";
import ReactDatePicker from "react-date-picker";
import { ScaleWithWidth } from "components/SimpleComponents/ScaleWithWidth";
import {
  BREAKPOINT_DESKTOP_XL,
  LANGUAGES,
  useBreakpoints,
} from "constants/constants";
import useWindowSize from "utils/customHooks/useWindowSize";
import { useIsDarkMode } from "store/atoms";
import { useSettings } from "contexts/SettingsManager/SettingsManager";

export const REACT_CALENDAR_FORMATS = {
  // formatDay: (locale, date) => format(date, "d"),
  // formatMonth: (locale, date) => format(date, "LLLL"),
  // formatYear: (locale, date) => format(date, "yyyy"),
  // formatLongDate: (locale, date) => format(date, "LLLL d, yyyy"),
  // formatMonthYear: (locale, date) => format(date, "LLLL yyyy"),
  // formatWeekday: (locale, date) => format(date, "EEEE"),
  // formatShortWeekday: (locale, date) => format(date, "ccccc"),
  formatDay: (locale, date) =>
    new Intl.DateTimeFormat(locale, { day: "numeric" }).format(date), // "d"
  formatMonth: (locale, date) =>
    new Intl.DateTimeFormat(locale, { month: "long" }).format(date), // "LLLL"
  formatYear: (locale, date) =>
    new Intl.DateTimeFormat(locale, { year: "numeric" }).format(date), // "yyyy"
  formatLongDate: (locale, date) =>
    new Intl.DateTimeFormat(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(date), // "LLLL d, yyyy"
  formatMonthYear: (locale, date) =>
    new Intl.DateTimeFormat(locale, { year: "numeric", month: "long" }).format(
      date
    ), // "LLLL yyyy"
  formatWeekday: (locale, date) =>
    new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date), // "EEEE"
  formatShortWeekday: (locale, date) =>
    new Intl.DateTimeFormat(locale, { weekday: "narrow" }).format(date), // "ccccc"
};

const MAP_LANGUAGEPREFERENCE_TO_LOCALE: {
  [lng in keyof typeof LANGUAGES]: string;
} = {
  fr: "fr-FR",
  "fr-CA": "fr-CA",
  en: "en-US",
  "es-419": "es-419",
};
/** uses [react-date-picker](https://github.com/wojtekmaj/react-date-picker) library */
export function DatePickerCalendar({
  open,
  setOpen,
  onChange,
  reactDatePickerGe,
  minDate,
  maxDate,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  onChange: (nextDate: Date) => void;
  reactDatePickerGe: Date | null;
  minDate: Date;
  maxDate: Date;
}) {
  // const formatZoned = useFormatZoned();
  const { isDesktopXlOrLarger } = useBreakpoints();
  const { innerWidth } = useWindowSize();
  const scale = Math.max(1, innerWidth / BREAKPOINT_DESKTOP_XL);
  const [isDarkMode] = useIsDarkMode();
  const { languagePreference } = useSettings();
  const locale =
    MAP_LANGUAGEPREFERENCE_TO_LOCALE[
      languagePreference as keyof typeof MAP_LANGUAGEPREFERENCE_TO_LOCALE
    ];
  return (
    <DatePickerCalendarStyles
      className="date-picker-calendar"
      $scale={scale}
      $isDarkMode={isDarkMode}
    >
      <ScaleWithWidth
        baseWidth={BREAKPOINT_DESKTOP_XL / 2}
        transformOrigin="center"
        enable={isDesktopXlOrLarger}
      >
        <ReactDatePicker
          format="yyyy-MM-dd"
          /**
           * If these format functions are removed, a bug occurs in iOS only
           * where the ErrorBoundary will be triggered and the UI obscured, hampering usage of the calendar.
           */
          {...REACT_CALENDAR_FORMATS}
          className="datePickerButton"
          calendarClassName="datePickerCalendar"
          isOpen={open}
          onCalendarClose={() => setOpen(false)}
          onChange={value => {
            onChange(value as Date);
          }}
          value={reactDatePickerGe}
          minDate={minDate}
          maxDate={maxDate}
          calendarType={"gregory"}
          onClickDay={(_date, event) => {
            event.stopPropagation();
          }}
          prevLabel={
            <ChevronRight
              style={{
                transform: "rotate(180deg)",
              }}
            />
          }
          nextLabel={<ChevronRight />}
          minDetail={"decade"}
          locale={locale}
        />
      </ScaleWithWidth>
    </DatePickerCalendarStyles>
  );
}
const DatePickerCalendarStyles = styled.div<{
  $scale: number;
  $isDarkMode: boolean;
}>`
  @media (max-width: 600px) {
    .react-date-picker__calendar {
      inset: 100% -50% auto auto !important;
    }
  }
  .react-calendar {
    transform: scale(${p => p.$scale});
    transform-origin: top right;
    border: 1px solid ${lightBorder3};
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0px 4px 8px -2px #0000001a;
    .react-calendar__navigation__next2-button,
    .react-calendar__navigation__prev2-button {
      display: none;
    }
    .react-calendar__tile.react-calendar__tile--now {
      border: 1px dashed ${brandPrimary70};
      border-radius: 4px;
    }
    button.react-calendar__navigation__arrow:disabled {
      opacity: 0.4;
    }
    .react-calendar__tile {
      width: 44px;
      height: 36px;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background: none;
      font-size: 14px;
      border-radius: 4px;
      font-weight: normal;
      color: ${lightTextBody};
      &:hover {
        background: #e6e6e6;
      }
      &:focus {
        outline: 2px solid ${brandPrimary70};
        outline-offset: -1px;
      }
      &:focus-visible {
        outline: 2px solid ${brandPrimary70};
        outline-offset: -1px;
      }
    }
    .react-calendar__navigation {
      margin-bottom: 20px;
      height: 28px;
    }
    .react-calendar__navigation__arrow {
      padding: 0;
      height: 28px;
      width: 28px;
      min-width: 0;
      background: none;
      .MuiSvgIcon-root {
        color: ${brandPrimary70};
        width: 28px;
        height: 28px;
      }
    }
    .react-calendar__navigation__label {
      padding: 0;
      height: 28px;
      background: none;
      font-size: 14px;
      font-family: "Open Sans", sans-serif;
    }
    .react-calendar__month-view__weekdays {
      margin-bottom: 12px;
      height: 28px;
    }
    .react-calendar__month-view__weekdays__weekday {
      background: ${lightNeutral10};
      color: ${brandPrimary70};
      &:first-child {
        border-radius: 6px 0 0 0;
      }
      &:last-child {
        border-radius: 0 6px 0 0;
      }
      abbr {
        text-decoration: none;
        font-weight: normal;
      }
    }
    .react-calendar__tile--active {
      color: ${lightTextOnDark};
      background: ${brandPrimary70};
      pointer-events: none;
    }
    button:disabled {
      pointer-events: none;
    }
    .react-calendar__month-view__days__day:disabled {
      opacity: 0.2;
    }
    .react-calendar__month-view__days__day--neighboringMonth {
      visibility: hidden;
    }
    .react-calendar__tile--hasActive.react-calendar__year-view__months__month,
    .react-calendar__tile--hasActive.react-calendar__decade-view__years__year {
      color: ${lightTextOnDark};
      background: ${brandPrimary70};
    }
    .react-calendar__decade-view__years__year:disabled,
    .react-calendar__year-view__months__month:disabled {
      opacity: 0.5;
    }
    ${props =>
      props.$isDarkMode
        ? css`
            background: ${darkModeModalBackgroundColor};
            .react-calendar__month-view__weekdays__weekday {
              background: ${darkModeTitleBackgroundColor};
            }
            .react-calendar__tile.react-calendar__month-view__days__day:not(
                .react-calendar__tile--active
              ) {
              color: ${darkModeDarkTextColor};
            }
            .react-calendar__tile--active {
              background: ${brandPrimary70};
            }
            .react-calendar__tile:hover {
              background: ${darkModeBackgroundHoveredColor};
            }
          `
        : ""}
  }
`;
